import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Time, Distance, Pace } from "./pace-fns/pace-fns";
import { TimeEntryField } from "./common-components/DataEntry/TimeEntryField";
import { FieldId } from "./calculator.utility";
import { usePreferences, ThemeProps } from "./usePreferences";
import { PaceEntryField } from "./common-components/DataEntry/PaceEntryField";
import {
  useCalculatorState,
  updateCalculatorState,
} from "./CalculatorStateManager";
import { DistanceEntryField } from "./common-components/DataEntry/DistanceEntryField";
import {
  isPaceValid as validatePace,
  isDistanceValid as validateDistance,
  isTimeValid as validateTime,
} from "./calculator.utility";
import * as analytics from "./analytics";
import { createLogAppState } from "./analytics.utils";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }: ThemeProps) => theme.palette.background};
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: space-between;
  justify-content: center;
`;

const ControlItem = styled.div`
  padding: 10px 0 10px 0;
`;

type Props = {
  onSubmit(): void;
};

export const DataEntry = ({ onSubmit }: Props) => {
  const calculatorStateManager = useCalculatorState();
  const [preferences, _dispatch] = usePreferences();
  const [state, setState] = useState(
    calculatorStateManager.getCalculatorState()
  );
  const [pendingFieldState, setPendingFieldState] = useState({ ...state });
  const [isPaceValid, setPaceValid] = useState(false);
  const [isDistanceValid, setDistanceValid] = useState(false);
  const [isTimeValid, setTimeValid] = useState(false);

  useEffect(() => {
    return calculatorStateManager.addStateListener(setState);
  }, []);

  useEffect(() => {
    setPendingFieldState({ ...state });
    setPaceValid(validatePace(state.pace));
    setDistanceValid(validateDistance(state.distance));
    setTimeValid(validateTime(state.time));
  }, [state]);

  useEffect(() => {}, [pendingFieldState]);

  const onUpdateState = (fieldId: FieldId) => {
    calculatorStateManager.dispatch(
      updateCalculatorState({
        pendingState: pendingFieldState,
        fieldToCalculateId: fieldId,
      })
    );
  };

  const calculateSelectedField = (fieldId: FieldId) => {
    onSubmit();
    onUpdateState(fieldId);
    analytics.logEvent("Calculate", fieldId, createLogAppState(pendingFieldState));
  };

  const onTimeChange = (isValid: boolean, time?: Time) => {
    if (isValid && time) {
      setPendingFieldState({
        ...pendingFieldState,
        time,
      });
    }
    setTimeValid(isValid);
  };

  const onDistanceChange = (isValid: boolean, distance: Distance) => {
    setPendingFieldState({
      ...pendingFieldState,
      distance,
    });
    setDistanceValid(isValid);
  };

  const onPaceChange = (isValid: boolean, pace: Pace) => {
    setPendingFieldState({
      ...pendingFieldState,
      pace,
    });
    setPaceValid(isValid);
  };

  return (
    <Container theme={preferences.theme}>
      <FieldContainer>
        <ControlItem>
          <TimeEntryField
            time={state.time}
            onChange={onTimeChange}
            selectField={() => calculateSelectedField(FieldId.Time)}
            isCalculable={isDistanceValid && isPaceValid}
          />
        </ControlItem>
        <ControlItem>
          <DistanceEntryField
            distance={state.distance}
            onChange={onDistanceChange}
            selectField={() => calculateSelectedField(FieldId.Distance)}
            isCalculable={isTimeValid && isPaceValid}
          />
        </ControlItem>
        <ControlItem>
          <PaceEntryField
            pace={state.pace}
            onChange={onPaceChange}
            selectField={() => calculateSelectedField(FieldId.Pace)}
            isCalculable={
              isTimeValid &&
              isDistanceValid &&
              !!pendingFieldState.pace.distance?.units
            }
          />
        </ControlItem>
      </FieldContainer>
    </Container>
  );
};
